<template>
  <div class="os-main pa-4">
    <!-- Cabeçalho -->
    <div class="d-flex align-center mb-4 flex-wrap" style="gap: 12px;">
      <h2 class="text-h5 font-weight-bold">Abertura de OS</h2>
      <v-spacer />
      <v-btn
        color="blue darken-1"
        dark
        :disabled="salvarDesabilitado || salvando"
        @click="salvarOsSelecionadas"
      >
        <v-icon left>mdi-check-circle</v-icon>
        {{ salvando ? "Salvando..." : "Salvar OS Selecionadas" }}
      </v-btn>
    </div>

    <!-- Card JOB -->
    <v-card class="mb-4" outlined>
      <v-card-title class="subtitle-1 font-weight-bold pb-0">
        <v-icon left color="blue darken-1">mdi-briefcase-outline</v-icon>
        JOB
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-autocomplete
              outlined
              dense
              hide-details
              label="Selecione o Job"
              :items="jobs"
              item-text="descricaoFormatada"
              item-value="codigo"
              :filter="filtrarJobs"
              return-object
              v-model="jobSelecionado"
              @change="onChangeJobSelecionado"
            >
              <template v-slot:selection="{ item }">{{ item.codigo }} — {{ item.descricao }}</template>
              <template v-slot:item="{ item }">{{ item.codigo }} — {{ item.descricao }}</template>
            </v-autocomplete>
          </v-col>

          <template v-if="revisaoSelecionada">
            <v-col cols="12" sm="4">
              <v-text-field
                :value="formatarMoeda(revisaoSelecionada.valorDisponivel)"
                label="Custo Previsto do Job (R$)"
                readonly
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                :value="formatarMoeda(custoDasOS)"
                label="Total das OSs Geradas (R$)"
                readonly
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                :value="formatarMoeda(saldoOS)"
                label="Saldo Disponível (R$)"
                readonly
                outlined
                dense
                hide-details
                :class="saldoOS < 0 ? 'saldo-negativo' : 'saldo-positivo'"
              />
            </v-col>
          </template>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Card Ordens de Serviço -->
    <v-card v-if="jobSelecionado" outlined>
      <v-card-title class="subtitle-1 font-weight-bold">
        <v-icon left color="green darken-1">mdi-hammer-wrench</v-icon>
        Ordens de Serviço
        <v-spacer />
        <v-btn small color="green darken-1" dark @click="adicionarNovaOS">
          <v-icon left small>mdi-plus</v-icon>
          Nova OS
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text v-if="ordensDeServicoJob.length === 0" class="text-center grey--text py-8">
        Nenhuma OS encontrada. Clique em "Nova OS" para adicionar.
      </v-card-text>

      <v-expansion-panels v-else accordion flat multiple class="rounded-0">
        <v-expansion-panel
          v-for="os in ordensDeServicoJob"
          :key="os._uid"
          class="os-panel"
        >
          <v-expansion-panel-header class="os-panel-header">
            <div class="d-flex align-center" style="gap: 10px; min-width: 0;">
              <v-checkbox
                v-model="osSelecionadas"
                :value="os"
                :disabled="!os._isDraft"
                hide-details
                dense
                class="mt-0 flex-shrink-0"
                @click.stop
              />
              <v-chip
                :color="os._isDraft ? 'orange darken-1' : 'blue darken-1'"
                dark
                small
                class="flex-shrink-0"
              >
                {{ os.codigo }}
              </v-chip>
              <span class="body-2 font-weight-medium flex-shrink-0">{{ os.osTipo || "—" }}</span>
              <span v-if="os.descricao" class="grey--text body-2 text-truncate">{{ os.descricao }}</span>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content class="os-panel-content">
            <v-row class="mt-1">

              <!-- Linha 1: Tipo | Job Item | Coordenador -->
              <v-col cols="12" sm="4">
                <v-select
                  v-model="os.osTipo"
                  :items="tiposDeOS"
                  label="Tipo de OS"
                  outlined
                  dense
                  hide-details
                  :disabled="!os._isDraft"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="os.jobItem"
                  :items="jobItensSelect"
                  item-text="descricaoFormatada"
                  item-value="id"
                  return-object
                  label="Job Item"
                  outlined
                  dense
                  hide-details
                  clearable
                  :disabled="!os._isDraft"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="os.coordenadorDeProjetos"
                  :items="coordenadoresDeProjetos"
                  item-text="nome"
                  item-value="id"
                  return-object
                  label="Coordenador de Projetos"
                  outlined
                  dense
                  hide-details
                  clearable
                  :disabled="!os._isDraft"
                />
              </v-col>

              <!-- Linha 2: Descrição | Valor Disponível | Quantidade -->
              <v-col cols="12" sm="4">
                <v-textarea
                  v-model="os.descricao"
                  label="Descrição"
                  outlined
                  dense
                  hide-details
                  rows="2"
                  no-resize
                  :disabled="!os._isDraft"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="os.valorDisponivelFormatado"
                  label="Valor Disponível"
                  prefix="R$"
                  outlined
                  dense
                  hide-details
                  :disabled="!os._isDraft"
                  @input="atualizarValorDisponivel(os, $event)"
                  @blur="formatarValorDisponivelOS(os)"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model.number="os.osQuantidade"
                  label="Quantidade"
                  type="number"
                  min="1"
                  outlined
                  dense
                  hide-details
                  :disabled="!os._isDraft"
                />
              </v-col>

              <!-- Linha 3: Encerramento Prevista | Encerramento Efetiva | (espaço) -->
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="os.dataEncerramentoPrevista"
                  label="Encerramento Prevista"
                  type="date"
                  outlined
                  dense
                  hide-details
                  :disabled="!os._isDraft"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="os.dataEncerramento"
                  label="Encerramento Efetiva"
                  type="date"
                  outlined
                  dense
                  hide-details
                  :disabled="!os._isDraft"
                />
              </v-col>
              <v-col cols="12" sm="4" />

              <!-- Linha 4: Possui Detalhamento | Data Detalhamento Prevista | Data Detalhamento Efetiva -->
              <v-col cols="12" sm="4" class="d-flex align-center">
                <v-checkbox
                  v-model="os.possuiDetalhamento"
                  label="Possui Detalhamento"
                  hide-details
                  dense
                  class="mt-0"
                  :disabled="!os._isDraft"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="os.dataDetalhamentoPrevista"
                  label="Detalhamento Prevista"
                  type="date"
                  outlined
                  dense
                  hide-details
                  :disabled="!os._isDraft || !os.possuiDetalhamento"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="os.dataDetalhamento"
                  label="Detalhamento Efetiva"
                  type="date"
                  outlined
                  dense
                  hide-details
                  :disabled="!os._isDraft || !os.possuiDetalhamento"
                />
              </v-col>

              <!-- Linha 5: Possui Montagem | Data Montagem Prevista | Data Montagem Efetiva -->
              <v-col cols="12" sm="4" class="d-flex align-center">
                <v-checkbox
                  v-model="os.possuiMontagem"
                  label="Possui Montagem"
                  hide-details
                  dense
                  class="mt-0"
                  :disabled="!os._isDraft"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="os.dataMontagemPrevista"
                  label="Montagem Prevista"
                  type="date"
                  outlined
                  dense
                  hide-details
                  :disabled="!os._isDraft || !os.possuiMontagem"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="os.dataMontagem"
                  label="Montagem Efetiva"
                  type="date"
                  outlined
                  dense
                  hide-details
                  :disabled="!os._isDraft || !os.possuiMontagem"
                />
              </v-col>

              <!-- Linha 6: Fabricação Prevista | Fabricação Efetiva | Ações -->
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="os.dataFabricacaoPrevista"
                  label="Fabricação Prevista"
                  type="date"
                  outlined
                  dense
                  hide-details
                  :disabled="!os._isDraft"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="os.dataFabricacao"
                  label="Fabricação Efetiva"
                  type="date"
                  outlined
                  dense
                  hide-details
                  :disabled="!os._isDraft"
                />
              </v-col>
              <v-col cols="12" sm="4" class="d-flex align-center justify-end" style="gap: 8px;">
                <v-btn
                  v-if="!os._isDraft && os.id"
                  color="blue darken-2"
                  dark
                  small
                  :loading="gerandoPdfOsId === os.id"
                  @click="gerarPdfOS(os)"
                >
                  <v-icon left small>mdi-file-pdf-box</v-icon>
                  Gerar PDF
                </v-btn>
                <v-btn
                  v-if="os._isDraft"
                  color="red darken-1"
                  dark
                  small
                  @click="deletarOS(os)"
                >
                  <v-icon left small>mdi-delete</v-icon>
                  Deletar OS
                </v-btn>
              </v-col>

            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>

  </div>
</template>

<script>
import viewService from "@/services/viewService";
import EntityService from "@/services/entityService";
import UtilRelatorioOSPDF from "@/utils/UtilRelatorioOSPDF";
import authService from "@/services/authService";

const NOVA_OS_CODIGO = "---";

export default {
  name: "OrdemDeServicoAberturaEdicaoView",
  data() {
    return {
      carregando: false,
      salvando: false,
      dadosView: null,
      jobs: [],
      coordenadoresDeProjetos: [],
      jobSelecionado: null,
      revisaoSelecionada: null,
      osSelecionadas: [],
      custoDasOS: 0,
      saldoOS: 0,
      gerandoPdfOsId: null,
      ordemDeServicoService: new EntityService("OrdemDeServico"),
      tiposDeOS: [
        "Padrão",
        "Fabricação",
        "Fabricação p/ Estoque",
        "Frete / Transporte",
        "Garantia de Vendas",
        "Levantamento de Campo",
        "Manut. Fabrica",
        "Manut. Veiculos",
        "Material de Escritório",
        "Material de Estoque",
        "Serviços de Engenharia",
        "Serviços de Montagem/Desmontagem",
        "Serviços"
      ]
    };
  },
  computed: {
    ordensDeServicoJob() {
      if (!this.jobSelecionado || !Array.isArray(this.jobSelecionado.ordensDeServico)) return [];
      return this.jobSelecionado.ordensDeServico;
    },
    jobItensSelect() {
      return Array.isArray(this.jobSelecionado?.jobItens) ? this.jobSelecionado.jobItens : [];
    },
    salvarDesabilitado() {
      if (!this.jobSelecionado) return true;
      if (!Array.isArray(this.osSelecionadas) || this.osSelecionadas.length === 0) return true;
      if (!this.osSelecionadas.some((os) => os?._isDraft)) return true;
      return false;
    }
  },
  methods: {
    aplicarFocoPorOsId() {
      const osId = this.$route?.query?.osId;
      if (!osId) return;

      const jobComOs = this.jobs.find((job) =>
        Array.isArray(job?.ordensDeServico) &&
        job.ordensDeServico.some((os) => String(os?.id || os?._id || '') === String(osId))
      );

      if (!jobComOs) {
        this.$notfy?.warning?.('OS não encontrada para foco automático.');
        return;
      }

      this.jobSelecionado = jobComOs;
      this.onChangeJobSelecionado();

      const osEncontrada = jobComOs.ordensDeServico.find(
        (os) => String(os?.id || os?._id || '') === String(osId)
      );

      if (osEncontrada) {
        this.osSelecionadas = [osEncontrada];
      }
    },

    normalizarDataISO(data = "") {
      if (!data) return "";
      return String(data).slice(0, 10);
    },
    gerarUid() {
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    },
    formatarMoeda(numero) {
      const valor = Number(numero || 0);
      return valor.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    converterTextoParaMoeda(valor = "") {
      const texto = String(valor ?? "").trim();
      if (!texto) return 0;

      const apenasNumeros = texto.replace(/\s/g, "");

      if (apenasNumeros.includes(",")) {
        const normalizado = apenasNumeros.replace(/\./g, "").replace(",", ".");
        const numero = Number(normalizado);
        return Number.isFinite(numero) ? numero : 0;
      }

      const numero = Number(apenasNumeros.replace(/\./g, ""));
      return Number.isFinite(numero) ? numero : 0;
    },
    atualizarValorDisponivel(os, valorDigitado = "") {
      const numero = this.converterTextoParaMoeda(valorDigitado);
      os.valorDisponivel = numero;
      os.valorDisponivelFormatado = valorDigitado;
      this.recalcularSaldo();
    },
    formatarValorDisponivelOS(os) {
      const numero = this.converterTextoParaMoeda(os?.valorDisponivelFormatado);
      os.valorDisponivel = numero;
      os.valorDisponivelFormatado = this.formatarMoeda(numero);
      this.recalcularSaldo();
    },
    normalizarTextoBusca(valor = "") {
      return String(valor || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
    },
    filtrarJobs(item, queryText) {
      const busca = this.normalizarTextoBusca(queryText);
      if (!busca) return true;

      const codigo = this.normalizarTextoBusca(item?.codigo);
      const descricao = this.normalizarTextoBusca(item?.descricao);
      const textoCompleto = this.normalizarTextoBusca(`${item?.codigo || ""} ${item?.descricao || ""}`);

      return codigo.includes(busca) || descricao.includes(busca) || textoCompleto.includes(busca);
    },
    obterIdSelecionado(valor) {
      if (!valor) return null;
      if (typeof valor === "string") return valor;
      return valor.id || null;
    },
    mapearOS(os = {}) {
      const codigo = os.codigo || os.code || NOVA_OS_CODIGO;
      const valorDisponivel = Number(os.valorDisponivel || 0);
      return {
        id: os.id || os._id || null,
        _uid: os._uid || os.id || this.gerarUid(),
        _isDraft: codigo === NOVA_OS_CODIGO,
        codigo,
        osTipo: os.osTipo || "Padrão",
        jobItem: os.jobItem || null,
        coordenadorDeProjetos: os.coordenadorDeProjetos || null,
        descricao: os.descricao || os.projetoDescricao || "",
        valorDisponivel,
        valorDisponivelFormatado: this.formatarMoeda(valorDisponivel),
        osQuantidade: Number(os.osQuantidade || 0),
        dataEncerramentoPrevista: this.normalizarDataISO(os.dataEncerramentoPrevista),
        dataEncerramento: this.normalizarDataISO(os.dataEncerramento),
        possuiDetalhamento: Boolean(os.possuiDetalhamento),
        dataDetalhamentoPrevista: this.normalizarDataISO(os.dataDetalhamentoPrevista),
        dataDetalhamento: this.normalizarDataISO(os.dataDetalhamento),
        possuiMontagem: Boolean(os.possuiMontagem),
        dataMontagemPrevista: this.normalizarDataISO(os.dataMontagemPrevista),
        dataMontagem: this.normalizarDataISO(os.dataMontagem),
        dataFabricacaoPrevista: this.normalizarDataISO(os.dataFabricacaoPrevista),
        dataFabricacao: this.normalizarDataISO(os.dataFabricacao)
      };
    },
    async carregarDados() {
      this.carregando = true;
      try {
        const response = await viewService.viewRun("os-abertura-e-edicao-de-os", 1, []);
        const data = response?.data?.data?.data || [];
        const primeiro = data[0] || {};

        this.dadosView = primeiro;
        this.coordenadoresDeProjetos = Array.isArray(primeiro.coordenadoresDeProjetos)
          ? primeiro.coordenadoresDeProjetos
          : [];

        this.jobs = (Array.isArray(primeiro.jobs) ? primeiro.jobs : [])
          .map((job) => {
            const ordens = Array.isArray(job.ordensDeServico)
              ? job.ordensDeServico.map((os) => this.mapearOS(os))
              : [];
            return {
              ...job,
              descricaoFormatada: `${job?.codigo || ""} — ${job?.descricao || ""}`.trim(),
              ordensDeServico: ordens
            };
          });

        this.aplicarFocoPorOsId();
      } catch (e) {
        this.$notfy?.error("Erro ao carregar dados de abertura/edição de OS");
      } finally {
        this.carregando = false;
      }
    },
    onChangeJobSelecionado() {
      this.revisaoSelecionada = this.jobSelecionado?.revisaoAtual || null;
      this.osSelecionadas = [];
      this.recalcularSaldo();
    },
    recalcularSaldo() {
      const valorJob = Number(this.revisaoSelecionada?.valorDisponivel || 0);
      const custo = this.ordensDeServicoJob.reduce((acc, os) => acc + Number(os.valorDisponivel || 0), 0);
      this.custoDasOS = custo;
      this.saldoOS = valorJob - custo;
    },
    adicionarNovaOS() {
      if (!this.jobSelecionado) return;
      const novo = this.mapearOS({
        id: null,
        codigo: NOVA_OS_CODIGO,
        osTipo: "Padrão",
        descricao: "",
        valorDisponivel: 0,
        osQuantidade: 0,
        dataEncerramentoPrevista: "",
        dataEncerramento: "",
        dataDetalhamentoPrevista: "",
        dataDetalhamento: "",
        possuiDetalhamento: false,
        dataMontagemPrevista: "",
        dataMontagem: "",
        possuiMontagem: false,
        dataFabricacaoPrevista: "",
        dataFabricacao: ""
      });
      this.jobSelecionado.ordensDeServico.unshift(novo);
      this.recalcularSaldo();
    },
    deletarOS(os) {
      const idx = this.jobSelecionado.ordensDeServico.findIndex((item) => item._uid === os._uid);
      if (idx >= 0) this.jobSelecionado.ordensDeServico.splice(idx, 1);
      this.osSelecionadas = this.osSelecionadas.filter((item) => item._uid !== os._uid);
      this.recalcularSaldo();
    },
    validarOsSelecionadas() {
      if (!this.jobSelecionado) return "Selecione um Job.";
      if (!this.osSelecionadas.length) return "Selecione pelo menos uma OS.";

      const selecionadasEditaveis = this.osSelecionadas.filter((os) => os?._isDraft);
      if (!selecionadasEditaveis.length) return "Selecione pelo menos uma OS nova para salvar.";

      const custoSelecionado = this.ordensDeServicoJob.reduce((acc, os) => acc + Number(os.valorDisponivel || 0), 0);
      if (custoSelecionado > Number(this.revisaoSelecionada?.valorDisponivel || 0)) {
        return "O total das OS excede o valor disponível da revisão do Job.";
      }

      for (const os of selecionadasEditaveis) {
        if (!os.codigo || !os.osTipo || !this.obterIdSelecionado(os.jobItem) || !this.obterIdSelecionado(os.coordenadorDeProjetos) || !os.descricao || !os.osQuantidade || !os.valorDisponivel || !os.dataEncerramentoPrevista || !os.dataFabricacaoPrevista) {
          return `Preencha todos os campos obrigatórios da OS ${os.codigo || NOVA_OS_CODIGO}.`;
        }

        if (os.possuiDetalhamento && !os.dataDetalhamentoPrevista) {
          return `Preencha Detalhamento Prevista da OS ${os.codigo || NOVA_OS_CODIGO}.`;
        }

        if (os.possuiMontagem && !os.dataMontagemPrevista) {
          return `Preencha Montagem Prevista da OS ${os.codigo || NOVA_OS_CODIGO}.`;
        }
      }

      return "";
    },
    async gerarCodigoOS() {
      const resposta = await this.ordemDeServicoService.autoInc("OrdemDeServico", "codigo", "######");
      const bruto = resposta?.data?.data || resposta?.data || "";
      const texto = String(bruto || "").trim().toUpperCase();
      if (!texto) throw new Error("Falha ao gerar código da OS");
      return texto;
    },
    async obterCompanySiteIdAtivo() {
      const local = window?.localStorage?.getItem("CompanySiteIdSelected");
      if (local) return local;
      try {
        const params = await this.$auth.getUserParams();
        return params?.companySiteIdDefault || null;
      } catch (_) {
        return null;
      }
    },
    toISODateString(valor = '') {
      if (!valor) return undefined;
      const texto = String(valor).trim();
      if (!texto) return undefined;
      const base = /^\d{4}-\d{2}-\d{2}$/.test(texto) ? texto + 'T00:00:00.000Z' : texto;
      const dt = new Date(base);
      return isNaN(dt.getTime()) ? undefined : dt.toISOString();
    },

    montarPayloadOS(os = {}, companySiteId = null, usuarioLogadoId = null) {
      const coordenadorId = this.obterIdSelecionado(os.coordenadorDeProjetos) || undefined;
      const ownerIds = [coordenadorId, usuarioLogadoId].filter(Boolean);
      const payload = {
        id: os.id || undefined,
        codigo: os.codigo,
        code: os.codigo,
        job: this.jobSelecionado?.id || this.jobSelecionado?._id || undefined,
        companySiteId: companySiteId || undefined,
        projetoDescricao: os.descricao,
        descricao: os.descricao,
        salesOrder: this.jobSelecionado?.solicitacaoDeOrcamento || undefined,
        valorDisponivel: Number(os.valorDisponivel || 0),
        osQuantidade: Number(os.osQuantidade || 0),
        osTipo: os.osTipo,
        jobItem: this.obterIdSelecionado(os.jobItem) || undefined,
        coordenadorDeProjetos: coordenadorId,
        dataSolicitacao: this.toISODateString(os.dataSolicitacao) || new Date().toISOString(),
        dataEncerramentoPrevista: this.toISODateString(os.dataEncerramentoPrevista),
        dataEncerramento: this.toISODateString(os.dataEncerramento),
        possuiDetalhamento: Boolean(os.possuiDetalhamento),
        dataDetalhamentoPrevista: this.toISODateString(os.dataDetalhamentoPrevista),
        dataDetalhamento: this.toISODateString(os.dataDetalhamento),
        possuiMontagem: Boolean(os.possuiMontagem),
        dataMontagemPrevista: this.toISODateString(os.dataMontagemPrevista),
        dataMontagem: this.toISODateString(os.dataMontagem),
        dataFabricacaoPrevista: this.toISODateString(os.dataFabricacaoPrevista),
        dataFabricacao: this.toISODateString(os.dataFabricacao),
        owner: ownerIds.length > 0 ? ownerIds : undefined
      };

      Object.keys(payload).forEach((chave) => {
        if (payload[chave] === undefined) delete payload[chave];
      });

      return payload;
    },
    async salvarOsSelecionadas() {
      const erroValidacao = this.validarOsSelecionadas();
      if (erroValidacao) {
        this.$notfy?.warning(erroValidacao);
        return;
      }

      this.salvando = true;

      try {
        const [companySiteId, usuarioParams] = await Promise.all([
          this.obterCompanySiteIdAtivo(),
          authService.getUserParams().catch(() => null)
        ]);
        const usuarioLogadoId = usuarioParams?.userId || usuarioParams?.id || null;

        for (const osSelecionada of this.osSelecionadas.filter((os) => os?._isDraft)) {
          const os = osSelecionada;

          if (!os.id || os.codigo === NOVA_OS_CODIGO) {
            os.codigo = await this.gerarCodigoOS();
          }

          const payload = this.montarPayloadOS(os, companySiteId, usuarioLogadoId);
          const response = await this.ordemDeServicoService.insertOrUpdate(payload, {});
          const itemPersistido = response?.data?.data || response?.data || {};

          os.id = itemPersistido.id || os.id;
          os.codigo = itemPersistido.codigo || os.codigo;
          os._isDraft = false;
        }

        this.$notfy?.success("OS salvas com sucesso.");
        await this.carregarDados();
        this.jobSelecionado = null;
        this.revisaoSelecionada = null;
        this.osSelecionadas = [];
        this.custoDasOS = 0;
        this.saldoOS = 0;
      } catch (e) {
        this.$notfy?.error("Erro ao salvar OS. Verifique os dados e tente novamente.");
      } finally {
        this.salvando = false;
      }
    },

    async gerarPdfOS(os) {
      if (!os || !os.id) {
        this.$notfy?.warning("Esta OS ainda não foi salva e não possui itens para gerar o relatório.");
        return;
      }
      this.gerandoPdfOsId = os.id;
      try {
        await UtilRelatorioOSPDF.gerarRelatorioOS(os);
      } catch (e) {
        console.error(e);
        this.$notfy?.error("Erro ao gerar PDF da OS. Verifique os dados e tente novamente.");
      } finally {
        this.gerandoPdfOsId = null;
      }
    }
  },
  mounted() {
    this.carregarDados();
  }
};
</script>

<style scoped>
.os-main {
  max-width: 100%;
  box-sizing: border-box;
}

.os-panel {
  border-bottom: 1px solid #e0e0e0;
}

.os-panel-header {
  min-height: 52px !important;
}

.os-panel-content {
  background-color: #fafafa;
}

.saldo-negativo >>> input {
  color: #d32f2f !important;
  font-weight: bold;
}

.saldo-positivo >>> input {
  color: #2e7d32 !important;
  font-weight: bold;
}
</style>
